<script>
import { mapGetters } from 'vuex';
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton.vue';
import MultiselectDropdownItems from 'shared/components/ui/MultiselectDropdownItems.vue';
import ThumbnailGroup from 'dashboard/components/widgets/ThumbnailGroup.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import validations from './helpers/validations';
import { useVuelidate } from '@vuelidate/core';

export default {
  components: {
    WootSubmitButton,
    MultiselectDropdownItems,
    ThumbnailGroup,
    NextButton,
  },

  props: {
    onSubmit: {
      type: Function,
      default: () => {},
    },
    submitInProgress: {
      type: Boolean,
      default: false,
    },
    formData: {
      type: Object,
      default: () => {},
    },
    submitButtonText: {
      type: String,
      default: '',
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    const formData = this.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = true,
      restrict_to_own_messages: restrictToOwnMessages = false,
      leader_ids: initialLeaderIds = [],
    } = formData;

    return {
      description,
      title,
      allowAutoAssign,
      restrictToOwnMessages,
      selectedLeaders: [],
      initialLeaderIds,
      showLeadersDropdown: false,
    };
  },
  validations,
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
    }),
    leaderThumbnails() {
      return this.selectedLeaders.slice(0, 4);
    },
    remainingLeaderCount() {
      return Math.max(this.selectedLeaders.length - 4, 0);
    },
    moreLeadersText() {
      if (this.remainingLeaderCount > 1) {
        return this.$t('TEAMS_SETTINGS.FORM.LEADERS.REMAINING_PLURAL', {
          count: this.remainingLeaderCount,
        });
      }
      return this.$t('TEAMS_SETTINGS.FORM.LEADERS.REMAINING_SINGULAR', {
        count: 1,
      });
    },
  },
  watch: {
    agents() {
      this.hydrateLeadersFromIds();
    },
    'formData.leader_ids': {
      immediate: false,
      handler(newIds = []) {
        this.initialLeaderIds = newIds;
        this.hydrateLeadersFromIds();
      },
    },
  },
  mounted() {
    this.$store.dispatch('agents/get');
    this.hydrateLeadersFromIds();
  },
  methods: {
    hydrateLeadersFromIds() {
      if (!this.initialLeaderIds || !this.initialLeaderIds.length) {
        this.selectedLeaders = [];
        return;
      }
      const byId = new Set(this.initialLeaderIds);
      this.selectedLeaders = (this.agents || []).filter(agent =>
        byId.has(agent.id)
      );
    },
    toggleLeadersDropdown() {
      this.showLeadersDropdown = !this.showLeadersDropdown;
    },
    closeLeadersDropdown() {
      this.showLeadersDropdown = false;
    },
    onLeaderSelect(agent) {
      const exists = this.selectedLeaders.some(a => a.id === agent.id);
      if (exists) {
        this.selectedLeaders = this.selectedLeaders.filter(
          a => a.id !== agent.id
        );
      } else {
        this.selectedLeaders = [...this.selectedLeaders, agent];
      }
    },
    removeLeader(agentId) {
      this.selectedLeaders = this.selectedLeaders.filter(
        a => a.id !== agentId
      );
    },
    handleSubmit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.onSubmit({
        description: this.description,
        name: this.title,
        allow_auto_assign: this.allowAutoAssign,
        restrict_to_own_messages: this.restrictToOwnMessages,
        leader_ids: this.selectedLeaders.map(a => a.id),
      });
    },
  },
};
</script>

<template>
  <div class="mx-0 flex flex-wrap">
    <div class="flex-shrink-0 flex-grow-0 w-full md:w-[65%]">
      <form class="mx-0 flex flex-wrap" @submit.prevent="handleSubmit">
        <woot-input
          v-model.trim="title"
          :class="{ error: v$.title.$error }"
          class="w-full"
          :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
          :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
          @input="v$.title.$touch"
        />

        <woot-input
          v-model.trim="description"
          :class="{ error: v$.description.$error }"
          class="w-full"
          :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
          @input="v$.description.$touch"
        />

        <div class="w-full flex items-center gap-2">
          <input v-model="allowAutoAssign" type="checkbox" :value="true" />
          <label for="conversation_creation">
            {{ $t('TEAMS_SETTINGS.FORM.AUTO_ASSIGN.LABEL') }}
          </label>
        </div>

        <div class="w-full flex items-center gap-2">
          <input
            id="restrict_to_own_messages"
            v-model="restrictToOwnMessages"
            type="checkbox"
          />
          <label for="restrict_to_own_messages">
            {{ $t('TEAMS_SETTINGS.FORM.RESTRICT_TO_OWN_MESSAGES.LABEL') }}
          </label>
        </div>

        <div class="w-full mt-4">
          <label class="block font-semibold text-sm mb-1">
            {{ $t('TEAMS_SETTINGS.FORM.LEADERS.LABEL') }}
          </label>
          <p class="text-xs text-n-slate-11 mb-2">
            {{ $t('TEAMS_SETTINGS.FORM.LEADERS.DESCRIPTION') }}
          </p>
          <div class="relative">
            <div
              class="flex items-center justify-between border border-n-weak rounded-md p-2 cursor-pointer"
              @click="toggleLeadersDropdown"
            >
              <div class="flex items-center gap-2 flex-1">
                <ThumbnailGroup
                  v-if="selectedLeaders.length"
                  :more-thumbnails-text="moreLeadersText"
                  :show-more-thumbnails-count="remainingLeaderCount > 0"
                  :users-list="leaderThumbnails"
                />
                <span v-if="selectedLeaders.length" class="text-sm">
                  {{
                    $t('TEAMS_SETTINGS.FORM.LEADERS.SELECTED_COUNT', {
                      count: selectedLeaders.length,
                    })
                  }}
                </span>
                <span v-else class="text-sm text-n-slate-10">
                  {{ $t('TEAMS_SETTINGS.FORM.LEADERS.PLACEHOLDER') }}
                </span>
              </div>
              <span class="i-lucide-chevron-down text-n-slate-11" />
            </div>
            <div
              v-if="showLeadersDropdown"
              v-on-clickaway="closeLeadersDropdown"
              class="absolute top-full left-0 right-0 mt-1 z-50 bg-n-alpha-3 border border-n-strong rounded-md shadow-lg p-2"
            >
              <div class="flex items-center justify-between mb-1">
                <h4 class="m-0 text-sm text-n-slate-12">
                  {{ $t('TEAMS_SETTINGS.FORM.LEADERS.DROPDOWN_TITLE') }}
                </h4>
                <NextButton
                  ghost
                  slate
                  xs
                  type="button"
                  icon="i-lucide-x"
                  @click="closeLeadersDropdown"
                />
              </div>
              <MultiselectDropdownItems
                :options="agents"
                :selected-items="selectedLeaders"
                has-thumbnail
                @select="onLeaderSelect"
              />
            </div>
          </div>
          <div
            v-if="selectedLeaders.length"
            class="flex flex-wrap gap-2 mt-2"
          >
            <span
              v-for="leader in selectedLeaders"
              :key="leader.id"
              class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-n-alpha-3 text-xs"
            >
              {{ leader.name }}
              <button
                type="button"
                class="i-lucide-x text-xs"
                :aria-label="$t('TEAMS_SETTINGS.FORM.LEADERS.REMOVE')"
                @click="removeLeader(leader.id)"
              />
            </span>
          </div>
        </div>

        <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full">
          <div class="w-full">
            <WootSubmitButton
              :disabled="v$.title.$invalid || submitInProgress"
              :button-text="submitButtonText"
              :loading="submitInProgress"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
